import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest,
} from '@/core/http-methods';
import {
  ChangeFieldValueType,
  TaskCommentsPayload,
  TaskDataType,
  TaskListElement,
} from '@/domain/task';
import { IhttpResponse } from '@/interfaces/http-interfaces';
import { CredentialsError, BackendError } from '../errors/api_errors';
import globalStore from '../store/globalStore';
import { DeepSearchQueryParams } from '@/domain/basic';

const TASK_URL = `${import.meta.env.VITE_RADAR_URL}/task/`;

export const getTasks = async (
  queryParams: DeepSearchQueryParams = {},
  signal?: AbortSignal
): Promise<TaskListElement[]> => {
  const url = new URL(TASK_URL);

  const query = { ...queryParams };

  for (const key in query) {
    if (Object.prototype.hasOwnProperty.call(query, key)) {
      url.searchParams.set(
        key,
        String(query[key as keyof DeepSearchQueryParams])
      );
    }
  }

  const res: IhttpResponse = await getRequest(url.href, {}, {}, false, signal);
  if (res.status === 200) {
    const task = res.body as Array<TaskListElement>;
    globalStore.dispatch('changeTasks', task);
    return task;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const getTask = async (
  id: number,
  listed?: number
): Promise<TaskDataType | TaskListElement> => {
  const url = `${TASK_URL}${id}`;
  const queryParams = {} as { [p: string]: string };
  if (listed) {
    queryParams.listed = String(listed);
  }
  const res: IhttpResponse = await getRequest(url, queryParams);
  if (res.status === 200) {
    const task = res.body;
    if (listed) return task as TaskListElement;
    return task as TaskDataType;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const getTaskComments = async (
  payload: TaskCommentsPayload
): Promise<TaskDataType> => {
  const url = `${TASK_URL}${payload.taskId}/comments`;

  const res: IhttpResponse = await getRequest(url);
  if (res.status === 200) {
    const task = res.body as TaskDataType;
    return task;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const getTaskListed = async (id: number): Promise<TaskListElement> => {
  const url = `${TASK_URL}${id}?listed=1`;
  const res: IhttpResponse = await getRequest(url);
  if (res.status === 200) {
    const taskListed = res.body as TaskListElement;
    globalStore.dispatch('changeTaskListed', taskListed);
    return taskListed;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const putTask = async (
  id: number,
  values: ChangeFieldValueType[]
): Promise<TaskDataType> => {
  const url = `${TASK_URL}${id}`;
  const body = { values: values };
  const res: IhttpResponse = await putRequest(url, body);
  if (res.status === 200) {
    return res.body as TaskDataType;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const postTask = async (
  id: number,
  values: ChangeFieldValueType[]
): Promise<TaskDataType> => {
  const url = `${TASK_URL}`;
  const body = {
    form_id: id,
    values: values,
  };
  const res: IhttpResponse = await postRequest(url, body);
  if (res.status === 200) {
    return res.body as TaskDataType;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const openTask = async (id: number): Promise<boolean> => {
  const url = `${TASK_URL}${id}/open`;
  const body = {
    task_id: id,
  };
  const res: IhttpResponse = await postRequest(url, body);
  if ([200, 201, 204].includes(res.status)) {
    return true;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const closeTask = async (id: number): Promise<boolean> => {
  const url = `${TASK_URL}${id}/close`;
  const body = {
    task_id: id,
  };
  const res: IhttpResponse = await postRequest(url, body);
  if ([200, 201, 204].includes(res.status)) {
    return true;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const revokeApprovalsTask = async (
  id: number,
  step_ids: number[]
): Promise<boolean> => {
  const url = `${TASK_URL}${id}/revoke-approvals`;
  const body = {
    steps: step_ids,
  };
  const res: IhttpResponse = await postRequest(url, body);
  if ([200, 201, 204].includes(res.status)) {
    return true;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const postTaskRoute = async (id: number, verdict: string) => {
  const url = `${TASK_URL}${id}/route`;
  const body = { verdict };

  const res: IhttpResponse = await postRequest(url, body);
  if (res.status === 200) {
    return res.body as TaskDataType;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const removeTaskObserver = async (
  task_id: number,
  id: number,
  type: string
) => {
  const url = `${TASK_URL}${task_id}/spectators`;
  const body = { id, type };

  const res: IhttpResponse = await deleteRequest(url, body);
  if (res.status === 200) {
    return res.body as TaskDataType;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const addTaskObserver = async (
  task_id: number,
  id: number,
  type: string
) => {
  const url = `${TASK_URL}${task_id}/spectators`;
  const body = { id, type };

  const res: IhttpResponse = await postRequest(url, body);
  if (res.status === 200) {
    return res.body as TaskDataType;
  }
  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const addTaskApprover = async (
  id: number,
  type: string,
  step_id: number,
  task_id: number
) => {
  const url = `${TASK_URL}${task_id}/approvers/${step_id}`;
  const body = { id, type };

  const res: IhttpResponse = await postRequest(url, body);
  if (res.status === 200) {
    return res.body as TaskDataType;
  }

  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const removeTaskApprover = async (
  id: number,
  type: string,
  step_id: number,
  task_id: number
) => {
  const url = `${TASK_URL}${task_id}/approvers/${step_id}`;
  const body = { id, type };

  const res: IhttpResponse = await deleteRequest(url, body);
  if (res.status === 200) {
    return res.body as TaskDataType;
  }

  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const setCommentRead = async (
  taskId: number,
  commentId: number,
  signal?: AbortSignal
) => {
  const url = `${TASK_URL}${taskId}/comments/${commentId}/set-read`;

  const res: IhttpResponse = await postRequest(url, {}, {}, false, signal);
  if (res.status === 200) {
    return res.body;
  }

  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};

export const setTasksRead = async (taskIds: number[]) => {
  const url = `${TASK_URL}set-read/`;
  
  if(Array.isArray(taskIds)  && taskIds.length === 0){
      throw new Error("ID`s array must not be empty")
  }

  const body = {
    tasks_ids: taskIds,
  };
  const res: IhttpResponse = await postRequest(url, body);
  if (res.status === 200) {
    return res.body;
  }

  if (res.status === 401) {
    throw new CredentialsError(JSON.stringify(res.body));
  }
  throw new BackendError(res.body);
};
